<script setup>
import PlayerRow from './PlayerRow.vue'
import PlayerDetailsModal from './PlayerDetailsModal.vue'
import TrimControls from './TrimControls.vue'
import MatchupProjectionModal from './MatchupProjectionModal.vue'
import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios'
import { apiBalancedProjectionsPath } from '../config/balancedProjectionsApi.js'
import { useProjectionsStore } from '../stores/projections'
import { useTeamMatchupStatsStore } from '../stores/teamMatchupStats'

const projectionsStore = useProjectionsStore()
const matchupStore = useTeamMatchupStatsStore()

const props = defineProps({
  roster: {
    type: Array,
    required: true
  },
  teamType: {
    type: String,
    required: true
  },
  teamColor: {
    type: String,
    required: true
  },
  statKeys: {
    type: Array,
    required: true
  }
})

const markets = ref([
    'points',
    'rebounds',
    'assists',
    'three_point_field_goal',
    'turnovers',
    'blocks',
    'steals'
])
const emit = defineEmits(['update:roster'])

const showAdjustmentModal = ref(false)
const adjustmentModalData = ref({ player: null, statKey: '', statLabel: '' })
const showPlayerDetailsModal = ref(false)
const selectedPlayerForDetails = ref(null)
const showMatchupModal = ref(false)

// Matchup projection state
const matchupProjections = ref(null)  // null = not loaded, {} with keys = loaded
const matchupLoading = ref(false)
const matchupError = ref(null)

// Helper to check if we have actual projection data
const hasMatchupProjections = computed(() => {
  return matchupProjections.value && Object.keys(matchupProjections.value).length > 0
})

// Stat key mapping from our stat keys to matchup stat keys
// Handle both with and without _per_30 suffix
const statKeyToMatchupKey = {
  // With _per_30 suffix (from NoProjectionsState)
  'points_per_30': 'ppg',
  'total_rebounds_per_30': 'rpg',
  'assists_per_30': 'apg',
  'three_point_field_goal_per_30': 'tpm',
  'turnovers_per_30': 'tov',
  'blocks_per_30': 'bpg',
  'steals_per_30': 'spg',
  // Without suffix (fallback)
  'points': 'ppg',
  'rebounds': 'rpg',
  'total_rebounds': 'rpg',
  'assists': 'apg',
  'three_point_field_goal': 'tpm',
  'turnovers': 'tov',
  'blocks': 'bpg',
  'steals': 'spg'
}

// Get matchup projection value for a stat
function getMatchupProjection(statKey) {
  const matchupKey = statKeyToMatchupKey[statKey]
  if (!matchupKey || !hasMatchupProjections.value) return null
  
  const projection = matchupProjections.value[matchupKey]
  return projection ? projection.projected?.toFixed(2) : null
}

// Get matchup MU% for styling
function getMatchupMuPercent(statKey) {
  const matchupKey = statKeyToMatchupKey[statKey]
  if (!matchupKey || !hasMatchupProjections.value) return 0
  
  const projection = matchupProjections.value[matchupKey]
  return projection ? projection.muPercent : 0
}

// Open matchup modal
function openMatchupModal() {
  showMatchupModal.value = true
}

// Close matchup modal
function closeMatchupModal() {
  showMatchupModal.value = false
}

// Load matchup data - auto-fetch from database if mappings exist
async function loadMatchupData() {
  if (!projectionsStore.projectionsData) return
  
  const homeTeam = projectionsStore.projectionsData.home_team?.team_name
  const awayTeam = projectionsStore.projectionsData.away_team?.team_name
  const league = projectionsStore.projectionsData.league?.league_name
  
  // OddsComposer IDs for persistent mapping
  const ocLeagueId = projectionsStore.projectionsData.league?.id
  const ocHomeTeamId = projectionsStore.projectionsData.home_team?.id
  const ocAwayTeamId = projectionsStore.projectionsData.away_team?.id
  
  if (!homeTeam || !awayTeam || !league) return
  
  matchupLoading.value = true
  matchupError.value = null
  
  try {
    // Step 1: Always fetch league mapping from backend for this fixture (no in-memory cache)
    const mapping = await matchupStore.checkLeagueMapping(league, ocLeagueId)
    if (!mapping) {
      matchupError.value = 'League mapping not configured'
      return
    }
    
    // Step 2: Ensure league stats are loaded
    if (!matchupStore.hasData && matchupStore.leagueMapping) {
      await matchupStore.fetchLeagueStats(matchupStore.leagueMapping.stats_league_id)
    }
    
    if (!matchupStore.hasData) {
      matchupError.value = 'League mapping not configured'
      return
    }
    
    // Step 3: Always fetch team mappings from backend for this fixture (no in-memory cache)
    const statsLeagueId = matchupStore.leagueMapping?.stats_league_id
    await matchupStore.checkTeamMapping(homeTeam, ocHomeTeamId, ocLeagueId, statsLeagueId)
    await matchupStore.checkTeamMapping(awayTeam, ocAwayTeamId, ocLeagueId, statsLeagueId)
    
    // Get mapped team names (check by ID first, then name, then use original)
    const statsHomeTeam = matchupStore.teamMappings[ocHomeTeamId] || matchupStore.teamMappings[homeTeam] || homeTeam
    const statsAwayTeam = matchupStore.teamMappings[ocAwayTeamId] || matchupStore.teamMappings[awayTeam] || awayTeam
    
    // Calculate matchup based on team type
    // Handle both naming conventions: 'home'/'away' and 'A'/'B'
    const isHomeTeam = props.teamType === 'home' || props.teamType === 'A'
    
    // For home team: home attacks away's defense → calcMatchup(home, away)
    // For away team: away attacks home's defense → calcMatchup(away, home)
    const attackingTeam = isHomeTeam ? statsHomeTeam : statsAwayTeam
    const defendingTeam = isHomeTeam ? statsAwayTeam : statsHomeTeam
    
    console.log(`🏀 MU Projection [${props.teamType}]:`, {
      isHomeTeam,
      attackingTeam,
      defendingTeam,
      hasTeamStats: !!matchupStore.getTeamStats(attackingTeam),
      hasOpponentStats: !!matchupStore.getOpponentStats(defendingTeam),
      teamMappings: { ...matchupStore.teamMappings }
    })
    
    matchupProjections.value = matchupStore.calculateMatchupLocally(attackingTeam, defendingTeam)
    
    console.log(`🏀 MU Projection [${props.teamType}] Result:`, matchupProjections.value)
    
    // If no projections calculated, teams might need mapping
    if (!matchupProjections.value) {
      matchupError.value = 'Team mapping required'
    }
  } catch (error) {
    console.error('Error loading matchup data:', error)
    matchupError.value = error.message
  } finally {
    matchupLoading.value = false
  }
}

// Periodically check and load matchup data until successful
let retryCount = 0
const maxRetries = 10

async function tryLoadMatchupData() {
  console.log(`📦 TeamRosterGrid [${props.teamType}] - tryLoad called, hasProjections:`, hasMatchupProjections.value, 'retries:', retryCount)
  
  if (hasMatchupProjections.value) {
    console.log(`✅ TeamRosterGrid [${props.teamType}] - Already has projections`)
    return
  }
  
  if (retryCount >= maxRetries) {
    console.log(`❌ TeamRosterGrid [${props.teamType}] - Max retries reached`)
    return
  }
  
  retryCount++
  console.log(`📦 TeamRosterGrid [${props.teamType}] - Attempt ${retryCount}/${maxRetries}`)
  
  try {
    await loadMatchupData()
    console.log(`📦 TeamRosterGrid [${props.teamType}] - After loadMatchupData, hasProjections:`, hasMatchupProjections.value)
  } catch (err) {
    console.error(`❌ TeamRosterGrid [${props.teamType}] - Error:`, err)
  }
  
  // If still no projections and we have more retries, try again
  if (!hasMatchupProjections.value && retryCount < maxRetries) {
    console.log(`📦 TeamRosterGrid [${props.teamType}] - Scheduling retry in 500ms`)
    setTimeout(tryLoadMatchupData, 500)
  }
}

// When fixture changes, clear matchup state and load fresh data from backend
watch(
  () => projectionsStore.projectionsData?.id,
  async (newId, oldId) => {
    if (newId != null && oldId != null && newId !== oldId) {
      retryCount = 0
      matchupProjections.value = null
      matchupStore.reset()
      await loadMatchupData()
    }
  }
)

onMounted(() => {
  console.log(`📦 TeamRosterGrid [${props.teamType}] mounted`)
  setTimeout(() => tryLoadMatchupData(), 100)
})

// Reload handler (for modal reload button) - full reset then fresh load from backend
async function reloadMatchupData() {
  matchupStore.reset()
  await loadMatchupData()
}

// Refs to access all PlayerRow instances
const playerRowRefs = ref([])

function openAdjustmentModal(player, statKey, statLabel, event) {
  adjustmentModalData.value = { player, statKey, statLabel }
  showAdjustmentModal.value = true
}

function closeAdjustmentModal() {
  showAdjustmentModal.value = false
}

function openPlayerDetailsModal(player) {
  selectedPlayerForDetails.value = player
  showPlayerDetailsModal.value = true
}

function closePlayerDetailsModal() {
  showPlayerDetailsModal.value = false
  selectedPlayerForDetails.value = null
}

function getPerThirtyValue(player, statKey) {
  if (!player || !player.projection) return null
  const proj = player.projection
  let value = null

  if (proj[`${statKey}_trim`] != null && proj[`${statKey}_trim`] !== '' && proj[`${statKey}_trim`] > 0) {
    value = proj[`${statKey}_trim`]
  } else if (proj[`${statKey}_manual`] != null && proj[`${statKey}_manual`] !== '' && proj[`${statKey}_manual`] > 0) {
    value = proj[`${statKey}_manual`]
  } else if (proj[`${statKey}_auto`] != null && proj[`${statKey}_auto`] !== '' && proj[`${statKey}_auto`] > 0) {
    value = proj[`${statKey}_auto`]
  } else if (proj[`${statKey}_original`] != null) {
    value = proj[`${statKey}_original`]
  }
  
  return value
}

function getRawStatValue(playerId, statKey) {
  const proj = props.roster.find(p => p.player.id === playerId)?.projection
  const rawKey = statKey.replace(/_per_30$/, '')
  if (!proj || proj[rawKey] == null) return '—'
  const num = Number(proj[rawKey])
  return !isNaN(num) ? num.toFixed(2) : proj[rawKey]
}

function getTotal(statKey) {
  return props.roster.reduce((sum, p) => {
    const val = p.projection?.[statKey] || 0
    return sum + (isNaN(Number(val)) ? 0 : Number(val))
  }, 0).toFixed(2)
}

function getRealTimeTotal(statKey) {
  return props.roster.reduce((sum, player) => {
    const perThirtyValue = getPerThirtyValue(player, statKey)
    if (perThirtyValue == null) return sum
    
    const minutes = player.projected_minutes?.value || 0
    if (!minutes) return sum
    
    const rawValue = (Number(perThirtyValue) / 30) * Number(minutes)
    return sum + (isNaN(rawValue) ? 0 : rawValue)
  }, 0).toFixed(2)
}

function getTotalProjectedMinutes() {
  return props.roster.reduce((sum, p) => sum + Number(p.projected_minutes?.value || 0), 0)
}

// PG=1, SG=2, SF=3, PF=4, C=5; sort by first position before comma, missing position last
const POSITION_ORDER = { PG: 1, SG: 2, SF: 3, PF: 4, C: 5 }
const POSITION_ORDER_MISSING = 99

function getPrimaryPositionRank(player) {
  const positionStr = player?.player?.position
  if (!positionStr || typeof positionStr !== 'string') return POSITION_ORDER_MISSING
  const firstPosition = positionStr.split(',')[0].trim()
  if (!firstPosition) return POSITION_ORDER_MISSING
  return POSITION_ORDER[firstPosition.toUpperCase()] ?? POSITION_ORDER_MISSING
}

// Roster sorted by first position before comma (PG first, then SG, SF, PF, C, then missing)
const sortedRoster = computed(() => {
  return [...props.roster].sort((a, b) => getPrimaryPositionRank(a) - getPrimaryPositionRank(b))
})

// Computed property to get all player IDs from the roster
const allPlayerIds = computed(() => {
  return props.roster.map(player => player?.player?.id)
})

// Helper function to get total for specific player IDs using the store function
function getTotalForPlayerIds(playerIds, statKey) {
  return projectionsStore.getFormattedProjectionValue(playerIds, statKey)
}

// Helper function to get total for all players in roster
function getTotalForAllPlayers(statKey) {
  return getTotalForPlayerIds(allPlayerIds.value, statKey)
}

const trimValues = ref({
  points: 0,
  rebounds: 0,
  assists: 0,
  three_point_field_goal: 0,
  turnovers: 0,
  blocks: 0,
  steals: 0
})

// Track locked state per stat key from TrimControls
const lockedStats = ref({})

function onTrimLockChange(statKey, isLocked) {
  lockedStats.value[statKey] = isLocked
}

function saveTrimValue(statKey) {
  const currentTotal = getRealTimeTotal(statKey)
  const desiredTotal = trimValues.value[statKey]
  const difference = desiredTotal - currentTotal
  
  console.log(`Stat: ${statKey}, Current: ${currentTotal}, Desired: ${desiredTotal}, Difference: ${difference}`)
  
  // If no difference, no need to adjust
  if (Math.abs(difference) < 0.01) return
  
  // Get all players with valid projections for this stat
  const playersWithProjections = props.roster.filter(player => {
    const perThirtyValue = getPerThirtyValue(player, statKey)
    return perThirtyValue != null && perThirtyValue > 0
  })
  
  if (playersWithProjections.length === 0) return
  
  // Calculate total current projection value to determine proportional distribution
  const totalCurrentProjection = playersWithProjections.reduce((sum, player) => {
    const perThirtyValue = getPerThirtyValue(player, statKey)
    const minutes = player.projected_minutes?.value || 0
    return sum + ((Number(perThirtyValue) / 30) * Number(minutes))
  }, 0)
  
  // Apply proportional adjustments to each player
  projectionsStore.projectionsData.fixture_projections.forEach(projection => {
    const player = props.roster.find(p => p.player.id === projection.player_id)
    if (!player) return
    
    const perThirtyValue = getPerThirtyValue(player, statKey)
    if (perThirtyValue == null || perThirtyValue <= 0) return
    
    const minutes = player.projected_minutes?.value || 0
    if (!minutes) return
    
    const currentPlayerProjection = (Number(perThirtyValue) / 30) * Number(minutes)
    const playerProportion = currentPlayerProjection / totalCurrentProjection
    const playerAdjustment = difference * playerProportion
    
    // Apply the adjustment to the trim value
    const currentTrimValue = projection[`${statKey}_trim`] || projection[`${statKey}_original`] || 0
    projection[`${statKey}_trim`] = Number(currentTrimValue) + (playerAdjustment * 30 / Number(minutes))
  })
   

  axios.post(apiBalancedProjectionsPath('projections/update'), {
    projections: projectionsStore.projectionsData.fixture_projections,
    fixture_id: projectionsStore.projectionsData.id
  })
}

// Global trigger function to check stat thresholds for all players
const triggerAllStatThresholds = async () => {
  console.log('Triggering stat thresholds for all players...')
  const promises = playerRowRefs.value.map(playerRowRef => {
    if (playerRowRef && playerRowRef.checkStatThresholdsManualTrigger) {
      return playerRowRef.checkStatThresholdsManualTrigger()
    }
  }).filter(Boolean)
  
  try {
    await Promise.all(promises)
    console.log('All stat thresholds checked successfully')
  } catch (error) {
    console.error('Error checking stat thresholds:', error)
  }
}

// Helper function to sort games by date (newest first) - same as PlayerGameStatsTable
function sortGamesByDate(games) {
  if (!games || games.length === 0) return games
  return [...games].sort((a, b) => {
    const dateA = new Date(a.game_date || a.datetime || 0)
    const dateB = new Date(b.game_date || b.datetime || 0)
    return dateB - dateA // Newest first
  })
}

// Helper function to calculate stats from games (exact same logic as PlayerStatsSummary)
function calculateStatsFromGames(games, projectedMinutes) {
  if (!games || games.length === 0) {
    return {
      points: { total: 0, per30: 0, projected: 0 },
      rebounds: { total: 0, per30: 0, projected: 0 },
      assists: { total: 0, per30: 0, projected: 0 },
      '3pt': { total: 0, per30: 0, projected: 0 },
      pra: { total: 0, per30: 0, projected: 0 },
      turnovers: { total: 0, per30: 0, projected: 0 },
      blocks: { total: 0, per30: 0, projected: 0 },
      steals: { total: 0, per30: 0, projected: 0 }
    }
  }

  // Sort games by date (newest first) - same as PlayerGameStatsTable does
  const sortedGames = sortGamesByDate(games)

  // Calculate sums using the exact same field names as PlayerStatsSummary
  const totalMinutes = sortedGames.reduce((sum, game) => sum + Number(game.minutes || 0), 0)
  const totalPoints = sortedGames.reduce((sum, game) => sum + Number(game.points || 0), 0)
  const totalRebounds = sortedGames.reduce((sum, game) => sum + Number(game.total_rebounds || 0), 0)
  const totalAssists = sortedGames.reduce((sum, game) => sum + Number(game.assists || 0), 0)
  const totalThreePointers = sortedGames.reduce((sum, game) => sum + Number(game.three_point_field_goal || 0), 0)
  const totalSteals = sortedGames.reduce((sum, game) => sum + Number(game.steals || 0), 0)
  const totalBlocks = sortedGames.reduce((sum, game) => sum + Number(game.blocks || 0), 0)
  const totalTurnovers = sortedGames.reduce((sum, game) => sum + Number(game.turnovers || 0), 0)
  // Use pra field directly if available, otherwise calculate it
  const totalPRA = sortedGames.reduce((sum, game) => {
    if (game.pra != null) {
      return sum + Number(game.pra || 0)
    }
    // Fallback: calculate PRA if not in game data
    return sum + (Number(game.points || 0) + Number(game.total_rebounds || 0) + Number(game.assists || 0))
  }, 0)

  const projMinutes = Number(projectedMinutes || 0)

  // Calculate per 30 using formula: (sum of stat / sum of minutes) * 30
  // Return as string with toFixed(2) to match PlayerStatsSummary exactly
  const calculatePer30 = (totalStat) => {
    if (totalMinutes === 0) return '0.00'
    return ((totalStat / totalMinutes) * 30).toFixed(2)
  }

  // Calculate projection using formula: (per30 / 30) * projected_minutes
  // Return as string with toFixed(2) to match PlayerStatsSummary exactly
  const calculateProjection = (totalStat) => {
    if (totalMinutes === 0 || projMinutes === 0) return '0.00'
    const per30 = (totalStat / totalMinutes) * 30
    return ((per30 / 30) * projMinutes).toFixed(2)
  }

  // Return values matching PlayerStatsSummary structure exactly
  // per30 and projected are strings from toFixed(2), then converted to numbers in statsSummary
  return {
    points: {
      total: totalPoints,
      per30: calculatePer30(totalPoints),
      projected: calculateProjection(totalPoints)
    },
    rebounds: {
      total: totalRebounds,
      per30: calculatePer30(totalRebounds),
      projected: calculateProjection(totalRebounds)
    },
    assists: {
      total: totalAssists,
      per30: calculatePer30(totalAssists),
      projected: calculateProjection(totalAssists)
    },
    '3pt': {
      total: totalThreePointers,
      per30: calculatePer30(totalThreePointers),
      projected: calculateProjection(totalThreePointers)
    },
    pra: {
      total: totalPRA,
      per30: calculatePer30(totalPRA),
      projected: calculateProjection(totalPRA)
    },
    turnovers: {
      total: totalTurnovers,
      per30: calculatePer30(totalTurnovers),
      projected: calculateProjection(totalTurnovers)
    },
    blocks: {
      total: totalBlocks,
      per30: calculatePer30(totalBlocks),
      projected: calculateProjection(totalBlocks)
    },
    steals: {
      total: totalSteals,
      per30: calculatePer30(totalSteals),
      projected: calculateProjection(totalSteals)
    }
  }
}

// Global trigger function to calculate and update stats for all players
const isCalculatingAll = ref(false)
const calculationProgress = ref({ current: 0, total: 0 })

const triggerAllPlayerCalculations = async () => {
  if (isCalculatingAll.value) {
    console.log('Calculation already in progress...')
    return
  }

  isCalculatingAll.value = true
  calculationProgress.value.total = props.roster.length
  calculationProgress.value.current = 0

  console.log('Triggering backend calculations for all players...')

  try {
    const fixtureId = projectionsStore.projectionsData?.id
    if (!fixtureId) {
      console.error('Fixture ID not found')
      return
    }

    // Process players sequentially to avoid overwhelming the server
    for (const player of props.roster) {
      const playerId = player.player?.id
      if (!playerId) continue

      try {
        // Get player's filter data from store
        const playerProjection = projectionsStore.projectionsData?.fixture_projections?.find(
          p => p && p.player_id == playerId
        )

        const seasons = playerProjection?.seasons || []
        const selectedLeagues = playerProjection?.selected_leagues || []

        // Skip if no filters are set
        if (seasons.length === 0 && selectedLeagues.length === 0) {
          console.log(`Skipping player ${playerId} - no filters set`)
          calculationProgress.value.current++
          continue
        }

        // Fetch games for this player
        const gamesResponse = await axios.post('/api/players/games', {
          player_id: playerId,
          seasons: seasons,
          leagues: selectedLeagues,
          additional_filter: 'allGames',
          fixtureId: fixtureId
        })

        // Parse games data (handle different response structures)
        let games = []
        if (Array.isArray(gamesResponse.data)) {
          games = gamesResponse.data
        } else if (gamesResponse.data?.games) {
          games = gamesResponse.data.games
        } else if (gamesResponse.data?.data) {
          games = gamesResponse.data.data
        } else {
          games = Object.values(gamesResponse.data).filter(item => typeof item === 'object' && item.id)
        }

        // Get projected minutes
        const projectedMinutes = player.projected_minutes?.value || 
                                 playerProjection?.projected_minutes || 
                                 projectionsStore.getPlayerProjectedMinutes(playerId) || 
                                 0

        // Calculate stats from games (sorted by date first, same as modal)
        const sortedGames = sortGamesByDate(games)
        const calculatedStats = calculateStatsFromGames(sortedGames, projectedMinutes)

        // Create stats object matching PlayerStatsSummary structure exactly
        // Labels: 'Points', 'Rebounds', 'Assists', '3PT', 'PRA', 'Turnovers', 'Blocks', 'Steals'
        // Converted to: 'points', 'rebounds', 'assists', '3pt', 'pra', 'turnovers', 'blocks', 'steals'
        // Parse per30 and projected from strings (toFixed(2)) to numbers, just like PlayerStatsSummary does
        const statsSummary = {
          gameCount: sortedGames.length,
          projectedMinutes: projectedMinutes,
          stats: {
            points: {
              total: calculatedStats.points.total,
              per30: parseFloat(calculatedStats.points.per30),
              projected: parseFloat(calculatedStats.points.projected),
              games: sortedGames.length
            },
            rebounds: {
              total: calculatedStats.rebounds.total,
              per30: parseFloat(calculatedStats.rebounds.per30),
              projected: parseFloat(calculatedStats.rebounds.projected),
              games: sortedGames.length
            },
            assists: {
              total: calculatedStats.assists.total,
              per30: parseFloat(calculatedStats.assists.per30),
              projected: parseFloat(calculatedStats.assists.projected),
              games: sortedGames.length
            },
            '3pt': {
              total: calculatedStats['3pt'].total,
              per30: parseFloat(calculatedStats['3pt'].per30),
              projected: parseFloat(calculatedStats['3pt'].projected),
              games: sortedGames.length
            },
            pra: {
              total: calculatedStats.pra.total,
              per30: parseFloat(calculatedStats.pra.per30),
              projected: parseFloat(calculatedStats.pra.projected),
              games: sortedGames.length
            },
            turnovers: {
              total: calculatedStats.turnovers.total,
              per30: parseFloat(calculatedStats.turnovers.per30),
              projected: parseFloat(calculatedStats.turnovers.projected),
              games: sortedGames.length
            },
            blocks: {
              total: calculatedStats.blocks.total,
              per30: parseFloat(calculatedStats.blocks.per30),
              projected: parseFloat(calculatedStats.blocks.projected),
              games: sortedGames.length
            },
            steals: {
              total: calculatedStats.steals.total,
              per30: parseFloat(calculatedStats.steals.per30),
              projected: parseFloat(calculatedStats.steals.projected),
              games: sortedGames.length
            }
          }
        }

        // Prepare filter data to send to backend (exact same structure as PlayerDetailsModal)
        const filterData = {
          fixtureId: fixtureId,
          player_id: playerId,
          leagues: selectedLeagues,
          seasons: seasons,
          filteredGames: sortedGames.length,
          calculatedStats: statsSummary,
          totals: {
            points: statsSummary.stats.points.total,
            rebounds: statsSummary.stats.rebounds.total,
            assists: statsSummary.stats.assists.total,
            three_pointers: statsSummary.stats['3pt'].total,
            pra: statsSummary.stats.pra.total,
            turnovers: statsSummary.stats.turnovers.total,
            blocks: statsSummary.stats.blocks.total,
            steals: statsSummary.stats.steals.total
          },
          per30: {
            points: statsSummary.stats.points.per30,
            rebounds: statsSummary.stats.rebounds.per30,
            assists: statsSummary.stats.assists.per30,
            three_pointers: statsSummary.stats['3pt'].per30,
            pra: statsSummary.stats.pra.per30,
            turnovers: statsSummary.stats.turnovers.per30,
            blocks: statsSummary.stats.blocks.per30,
            steals: statsSummary.stats.steals.per30
          },
          projected: {
            points: statsSummary.stats.points.projected,
            rebounds: statsSummary.stats.rebounds.projected,
            assists: statsSummary.stats.assists.projected,
            three_pointers: statsSummary.stats['3pt'].projected,
            pra: statsSummary.stats.pra.projected,
            turnovers: statsSummary.stats.turnovers.projected,
            blocks: statsSummary.stats.blocks.projected,
            steals: statsSummary.stats.steals.projected
          }
        }

        // Send calculated stats to API
        const response = await axios.post(apiBalancedProjectionsPath('projections/stats-update'), filterData)

        // Update store with response
        const arr = projectionsStore.projectionsData.fixture_projections
        const idx = arr.findIndex(p => p.player_id === response.data.player_id)
        if (idx !== -1) {
          arr[idx] = response.data
        }

        console.log(`Calculated stats for player ${playerId}:`, response.data)
        calculationProgress.value.current++
      } catch (error) {
        console.error(`Error calculating stats for player ${player.player?.id}:`, error)
        calculationProgress.value.current++
      }
    }

    console.log('All player calculations completed successfully')
  } catch (error) {
    console.error('Error triggering all player calculations:', error)
  } finally {
    isCalculatingAll.value = false
    calculationProgress.value.current = 0
    calculationProgress.value.total = 0
  }
}

// Expose the global trigger functions to parent components
defineExpose({
  triggerAllStatThresholds,
  triggerAllPlayerCalculations
})
    
</script>

<template>
  <div class=" rounded-2xl shadow-lg bg-white">
    <!-- Header -->
    <div class="grid grid-cols-[50px_1fr_20px_100px_repeat(9,1fr)] gap-2 p-3 bg-gray-700 text-gray-100 font-semibold rounded-t-md">
      <div class="text-left">#</div>
      <div class="text-left">Player</div>
      <div class="text-left">S</div>
      <!-- <div class="text-center">Season Avg Minutes</div> -->
      <div class="text-center">Projected Minutes</div>
      <div v-for="stat in statKeys" :key="stat.key" class="text-center">{{ stat.label }}</div>
    </div>
    
    <!-- Global Trigger Buttons -->
    <div class="p-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2">
      <button 
        @click="triggerAllStatThresholds"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        🔍 Check All Stat Thresholds
      </button>
      <button 
        @click="triggerAllPlayerCalculations"
        :disabled="isCalculatingAll"
        class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <span v-if="isCalculatingAll" class="animate-spin">⏳</span>
        <span v-else>⚡</span>
        <span v-if="isCalculatingAll">
          Calculating... ({{ calculationProgress.current }}/{{ calculationProgress.total }})
        </span>
        <span v-else>Calculate All Player Stats</span>
      </button>
    </div>

    <!-- Rows -->
    <div class="divide-y divide-gray-100">
      <PlayerRow
        v-for="(player, idx) in sortedRoster"
        :key="player.player?.id"
        :ref="(el) => { if (el) playerRowRefs[idx] = el }"
        :player="player"
        :index="idx"
        :stat-keys="statKeys"
        :team-color="teamColor"
      />
    </div>

    <!-- Footer -->
    <div class="grid grid-cols-[50px_1fr_20px_100px_repeat(9,1fr)] gap-2 p-3 bg-gray-100 text-gray-900 font-bold border-t border-gray-200 rounded-b-md">
      <div class="text-center">#</div>
      <div class="text-right">Totals</div>
      <div class="text-left"></div>
      <div class="text-center">{{ getTotalProjectedMinutes() }}</div>
      <div v-for="stat in statKeys" :key="stat.key" class="text-center">
        {{ getTotalForAllPlayers(stat.key) }}
      </div>
    </div>
    <!-- Match up projections -->
    <div 
      @click="openMatchupModal"
      class="grid grid-cols-[50px_1fr_20px_100px_repeat(9,1fr)] gap-2 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 text-gray-900 font-semibold border-t border-purple-200 cursor-pointer transition-all duration-200 group"
      :class="{ 'opacity-50': matchupLoading }"
    >
      <div class="text-center">
        <i class="fas fa-calculator text-purple-500 group-hover:text-purple-700"></i>
      </div>
      <div class="text-right flex items-center justify-end gap-2">
        <span>MU Projection</span>
        <i class="fas fa-external-link-alt text-xs text-purple-400 group-hover:text-purple-600"></i>
      </div>
      <div class="text-center"></div>
      <div class="text-center text-purple-600 text-xs">
        {{ matchupLoading ? 'Loading...' : (matchupError ? '⚠️' : 'vs Opp') }}
      </div>
      <div 
        v-for="stat in statKeys" 
        :key="stat.key" 
        class="text-center font-mono"
        :class="{
          'text-green-600': getMatchupMuPercent(stat.key) > 0,
          'text-red-600': getMatchupMuPercent(stat.key) < 0,
          'text-gray-600': getMatchupMuPercent(stat.key) === 0
        }"
      >
        <template v-if="getMatchupProjection(stat.key)">
          {{ getMatchupProjection(stat.key) }}
          <template v-if="stat.key && stat.key.includes('rebound')">
            <span class="ml-1 text-xs text-gray-500">({{ (getMatchupProjection(stat.key) && !isNaN(Number(getMatchupProjection(stat.key))) ? (Number(getMatchupProjection(stat.key)) * 0.89).toFixed(2) : '-' ) }})</span>
          </template>
        
  
        </template>
        <template v-else>
          <span class="text-gray-400">-</span>
        </template>
      </div>
    </div>
    <!-- Match up projections -->
    <div 
      @click="openMatchupModal"
      class="grid grid-cols-[50px_1fr_20px_100px_repeat(9,1fr)] gap-2 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 text-gray-900 font-semibold border-t border-purple-200 cursor-pointer transition-all duration-200 group"
      :class="{ 'opacity-50': matchupLoading }"
    >
      <div class="text-center">
        <i class="fas fa-calculator text-purple-500 group-hover:text-purple-700"></i>
      </div>
      <div class="text-right flex items-center justify-end gap-2">
        <span>MU test Projection</span>
        <i class="fas fa-external-link-alt text-xs text-purple-400 group-hover:text-purple-600"></i>
      </div>
      <div class="text-center"></div>
      <div class="text-center text-purple-600 text-xs">
        {{ matchupLoading ? 'Loading...' : (matchupError ? '⚠️' : 'vs Opp') }}
      </div>
      <!-- {{
        (
          (Number(getTotalForAllPlayers('points_per_30')) * (1 + Number(getMatchupMuPercent('points')) / 100))
        ).toFixed(2)
      }} -->
      
      <div 
        v-for="stat in statKeys" 
        :key="stat.key" 
        class="text-center font-mono"
        :class="{
          'text-green-600': getMatchupMuPercent(stat.key) > 0,
          'text-red-600': getMatchupMuPercent(stat.key) < 0,
          'text-gray-600': getMatchupMuPercent(stat.key) === 0
        }"
      >
        <template v-if="getMatchupProjection(stat.key)">
          <!-- {{ getMatchupProjection(stat.key) }} -->
         
         <span v-if="!lockedStats[stat.key]">
           <span class="font-mono text-xs text-gray-400">
             <!-- Debug: 
               getTotalForAllPlayers({{ stat.key }})={{ getTotalForAllPlayers(stat.key) }},
               getMatchupMuPercent({{ stat.key }})={{ getMatchupMuPercent(stat.key) }},
               Formula: (total * (1 + muPercent / 100))
             -->
           </span>
           {{
             (
               (Number(getTotalForAllPlayers(stat.key)) * (1 + Number(getMatchupMuPercent(stat.key)) / 100))
             ).toFixed(2)
           }}

         
           <!-- <span class="font-mono text-2xs text-purple-400 ml-1" style="font-size:10px;">
             [{{ getTotalForAllPlayers(stat.key) }} × (1+{{ getMatchupMuPercent(stat.key) }}/100)]
           </span> -->
         </span>
         <span v-else class="text-gray-400">—</span>
  

          
        </template>
        <template v-else>
          <span class="text-gray-400">-</span>
        </template>
      </div>
    </div>
    <!-- this is the same as the footer but with the trim controls -->
    <div class="grid grid-cols-[50px_1fr_20px_100px_repeat(9,1fr)] gap-2 p-3 bg-gray-100 text-gray-900 font-bold border-t border-gray-200 rounded-b-md">
      <div class="text-center">#</div>
      <div class="text-right">Team projection</div>
      <div class="text-center"></div>
      <div class="text-center">{{ getTotalProjectedMinutes() }}</div>
      <div v-for="stat in statKeys" :key="stat.key" class="text-center flex flex-col items-center">
        <TrimControls
          :stat-key="stat.key"
          :current-total="Number(getRealTimeTotal(stat.key))"
          :roster="roster"
          :team-type="teamType"
          @update:locked="(isLocked) => onTrimLockChange(stat.key, isLocked)"
        />
      </div>
    </div>
  </div>

  <PlayerDetailsModal
    v-if="showPlayerDetailsModal && selectedPlayerForDetails"
    :player="selectedPlayerForDetails"
    :is-open="showPlayerDetailsModal"
    @update:isOpen="val => { if (!val) closePlayerDetailsModal() }"
  />

  <MatchupProjectionModal
    v-if="showMatchupModal"
    :is-open="showMatchupModal"
    :team-type="teamType"
    :matchup-projections="matchupProjections"
    :matchup-loading="matchupLoading"
    :matchup-error="matchupError"
    @close="closeMatchupModal"
    @reload="reloadMatchupData"
  />
</template> 